'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { PhoneCall, Plus, Search, CheckCircle, XCircle, Clock, Loader2, RefreshCw, Edit2, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/context/AuthContext';


interface CallbackSchedule {
  id: string;
  callId: string | null;
  patientId: string | null;
  patientName: string;
  phone: string;
  scheduledAt: string;
  assignedTo: string | null;
  notes: string;
  status: 'pending' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

const STATUS_CONFIG = {
  pending: { label: 'In Attesa', color: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const EMPTY_FORM: { patientName: string; phone: string; scheduledAt: string; assignedTo: string; notes: string; status: CallbackSchedule['status'] } = { patientName: '', phone: '', scheduledAt: '', assignedTo: '', notes: '', status: 'pending' };

export default function CallbacksPage() {
  const { session } = useAuth();
  const [callbacks, setCallbacks] = useState<CallbackSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), perPage: '20' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`/api/callbacks?${params}`);
      const json = await res.json();
      setCallbacks(json.data ?? json.callbacks ?? []);
      setTotal(json.total ?? 0);
    } catch (err: unknown) {
      console.error('[callbacks] load error:', err);
      setCallbacks([]);
    } finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.patientName.trim() || !form.phone.trim() || !form.scheduledAt) {
      showMsg('error', 'Nome paziente, telefono e data/ora sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const res = await fetch('/api/callbacks', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editId, ...form }),
        });
        if (!res.ok) throw new Error('Errore aggiornamento');
        showMsg('success', 'Callback aggiornato');
      } else {
        const res = await fetch('/api/callbacks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error('Errore creazione');
        showMsg('success', 'Callback creato');
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err: any) { showMsg('error', err.message); } finally { setSaving(false); }
  };

  const handleEdit = (cb: CallbackSchedule) => {
    setEditId(cb.id);
    setForm({
      patientName: cb.patientName,
      phone: cb.phone,
      scheduledAt: cb.scheduledAt ? cb.scheduledAt.slice(0, 16) : '',
      assignedTo: cb.assignedTo ?? '',
      notes: cb.notes,
      status: cb.status,
    });
    setShowForm(true);
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await fetch('/api/callbacks', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
      });
      showMsg('success', 'Stato aggiornato');
      load();
    } catch { showMsg('error', 'Errore aggiornamento stato'); }
  };

  const filtered = callbacks.filter(cb =>
    !search || cb.patientName.toLowerCase().includes(search.toLowerCase()) || cb.phone.includes(search)
  );

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <PhoneCall size={22} className="text-primary" /> Gestione Callback
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Richieste di richiamata: scheduling, esecuzione e storico</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                className="p-2 border border-border rounded-lg hover:bg-muted/50 min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Aggiorna callback"
              >
                <RefreshCw size={16} className="text-muted-foreground" aria-hidden="true" />
              </button>
              <button onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                <Plus size={16} aria-hidden="true" /> Nuovo Callback
              </button>
            </div>
          </div>

          {msg && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium border ${msg.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
            >
              {msg.text}
            </div>
          )}

          {showForm && (
            <div className="bg-white border border-border rounded-xl p-5 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">{editId ? 'Modifica Callback' : 'Nuovo Callback'}</h3>
                <button onClick={() => { setShowForm(false); setEditId(null); }} className="p-1 hover:bg-muted/50 rounded">
                  <X size={16} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Nome Paziente *</label>
                  <input value={form.patientName} onChange={e => setForm(p => ({ ...p, patientName: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Telefono *</label>
                  <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder="+39 333 1234567" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Data e Ora *</label>
                  <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(p => ({ ...p, scheduledAt: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Assegnato a</label>
                  <input value={form.assignedTo} onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder="Operatore" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Note</label>
                  <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    rows={2} className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none" placeholder="Note aggiuntive..." />
                </div>
                {editId && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Stato</label>
                    <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm">
                      <option value="pending">In Attesa</option>
                      <option value="completed">Completato</option>
                      <option value="cancelled">Annullato</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={handleSubmit} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-60">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  {editId ? 'Aggiorna' : 'Crea Callback'}
                </button>
                <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 border border-border rounded-lg text-sm">Annulla</button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm" placeholder="Cerca per nome o telefono..." />
            </div>
            <div className="flex items-center gap-1 border border-border rounded-lg overflow-hidden">
              {(['all', 'pending', 'completed', 'cancelled'] as const).map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50 text-muted-foreground'}`}>
                  {s === 'all' ? 'Tutti' : STATUS_CONFIG[s]?.label ?? s}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/30">
                <span className="text-sm text-muted-foreground font-medium">{filtered.length} callback</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    {['Paziente', 'Telefono', 'Programmato', 'Assegnato', 'Stato', 'Note', 'Azioni'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nessun callback trovato</td></tr>
                  ) : filtered.map(cb => {
                    const cfg = STATUS_CONFIG[cb.status];
                    const Icon = cfg.icon;
                    return (
                      <tr key={cb.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium text-foreground">{cb.patientName}</td>
                        <td className="px-4 py-3 font-mono text-sm text-muted-foreground">{cb.phone}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {cb.scheduledAt ? new Date(cb.scheduledAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{cb.assignedTo ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                            <Icon size={11} /> {cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs max-w-[200px] truncate">{cb.notes || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {cb.status === 'pending' && (
                              <button onClick={() => handleStatusChange(cb.id, 'completed')}
                                className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Segna completato">
                                <CheckCircle size={14} />
                              </button>
                            )}
                            <button onClick={() => handleEdit(cb)} className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground">
                              <Edit2 size={14} />
                            </button>
                            {cb.status === 'pending' && (
                              <button onClick={() => handleStatusChange(cb.id, 'cancelled')}
                                className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Annulla">
                                <XCircle size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
